/**
 * ProductSearchAdd — shared by CountSheetsPage and StockCountPanel.
 *
 * A search-and-add bar pinned at the bottom of the count grid.
 * Queries /api/count-sheets/{id}/searchable-products (excludes already-in-sheet items).
 * On select: calls /api/count-sheets/{id}/items/add-product.
 * 409 → toast "Product already in Line X" with optional jump callback.
 *
 * Props:
 *   sheetId    — count sheet DB id
 *   onAdded    — callback after a product is added (reload the sheet)
 *   onJumpTo   — (productId, lineNo) → scroll to the row
 *   disabled   — true if sheet is not in_progress
 */
import { useState, useEffect, useRef } from 'react';
import { api } from '../contexts/AuthContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductSearchAdd({ sheetId, onAdded, onJumpTo, disabled = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null); // product_id being added
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`${BACKEND_URL}/api/count-sheets/${sheetId}/searchable-products?q=${encodeURIComponent(query)}`);
        setResults(res.data || []);
        setOpen(true);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query, sheetId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (!containerRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const add = async (product) => {
    setAdding(product.id);
    try {
      const res = await api.post(`${BACKEND_URL}/api/count-sheets/${sheetId}/items/add-product`, { product_id: product.id });
      toast.success(`${product.name} added at Line ${res.data.line}`);
      setQuery(''); setResults([]); setOpen(false);
      onAdded?.();
      // Auto-jump to the newly added row
      setTimeout(() => onJumpTo?.(product.id, res.data.line), 400);
    } catch (e) {
      if (e?.response?.status === 409) {
        const msg = e.response.data?.detail || '';
        const lineMatch = msg.match(/line (\d+)/i);
        const lineNo = lineMatch ? parseInt(lineMatch[1]) : null;
        toast.warning(
          <span>
            {product.name} already in {lineNo ? (
              <button className="underline font-semibold" onClick={() => onJumpTo?.(product.id, lineNo)}>
                Line {lineNo}
              </button>
            ) : 'the sheet'}.
          </span>,
          { duration: 5000 }
        );
      } else {
        toast.error(e?.response?.data?.detail || 'Could not add product');
      }
    }
    setAdding(null);
  };

  if (disabled) return null;

  return (
    <div ref={containerRef} className="relative mt-2" data-testid="product-search-add">
      <div className="flex gap-2 items-center p-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg">
        <Search size={14} className="text-slate-400 shrink-0" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search product name or SKU to add..."
          className="h-8 text-sm border-0 bg-transparent focus:ring-0 p-0 flex-1"
          data-testid="product-search-input"
          onFocus={() => results.length > 0 && setOpen(true)}
        />
        {searching && <Loader2 size={13} className="animate-spin text-slate-400 shrink-0" />}
      </div>

      {open && results.length > 0 && (
        <div className="absolute bottom-full mb-1 left-0 right-0 z-50 bg-white rounded-lg border border-slate-200 shadow-xl max-h-60 overflow-y-auto"
          data-testid="product-search-results">
          {results.map(p => (
            <div key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{p.name}</p>
                <p className="text-[10px] text-slate-400">{p.sku} · {p.category} · {p.unit}</p>
              </div>
              <Button size="sm" variant="outline"
                onClick={() => add(p)}
                disabled={adding === p.id}
                className="h-7 px-2 shrink-0"
                data-testid={`add-product-${p.id}`}>
                {adding === p.id ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              </Button>
            </div>
          ))}
          {results.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No matching products</p>
          )}
        </div>
      )}

      {open && results.length === 0 && !searching && query.trim() && (
        <div className="absolute bottom-full mb-1 left-0 right-0 z-50 bg-white rounded-lg border border-slate-200 shadow-xl p-4 text-sm text-slate-400 text-center">
          No products found matching "{query}"
        </div>
      )}
    </div>
  );
}
